import { z } from 'zod';
export const EditableTableSourceRefSchema = z.object({
    section: z.enum(['header', 'body']),
    row: z.number().int().nonnegative(),
    cell: z.number().int().nonnegative().optional(),
});
const EditableTableCellBaseSchema = z.object({
    source_ref: EditableTableSourceRefSchema.nullish().default(null),
    column: z.number().int().nonnegative(),
    col_span: z.number().int().positive(),
    row_span: z.number().int().positive(),
    alignment: z.enum(['left', 'right', 'center', 'block']).optional(),
    vertical_alignment: z.enum(['top', 'center', 'bottom']).optional(),
});
const EditableTableTextCellSchema = EditableTableCellBaseSchema.extend({
    kind: z.literal('text'),
    paragraphs: z.array(z.string()),
});
const EditableTableImageCellSchema = EditableTableCellBaseSchema.extend({
    kind: z.literal('image'),
    image_index: z.number().int().nonnegative(),
});
export const EditableTableCellSchema = z.discriminatedUnion('kind', [EditableTableTextCellSchema, EditableTableImageCellSchema]);
export const EditableTableRowSchema = z.object({
    source_ref: EditableTableSourceRefSchema.nullish().default(null),
    template_source_ref: EditableTableSourceRefSchema.nullish().optional(),
    cells: z.array(EditableTableCellSchema),
});
export const EditableReportTableSchema = z
    .object({
    column_count: z.number().int().positive(),
    header: EditableTableRowSchema.nullish().default(null),
    rows: z.array(EditableTableRowSchema).min(1),
})
    .superRefine((table, context) => {
    const rows = [...(table.header ? [table.header] : []), ...table.rows];
    const grid = rows.map(() => Array(table.column_count).fill(false));
    let isValid = true;
    rows.forEach((row, rowIndex) => {
        row.cells.forEach((cell) => {
            if (cell.column + cell.col_span > table.column_count || rowIndex + cell.row_span > rows.length) {
                isValid = false;
                return;
            }
            for (let y = rowIndex; y < rowIndex + cell.row_span; y += 1) {
                for (let x = cell.column; x < cell.column + cell.col_span; x += 1) {
                    if (grid[y][x])
                        isValid = false;
                    grid[y][x] = true;
                }
            }
        });
    });
    if (!isValid || grid.some((row) => row.some((cell) => !cell))) {
        context.addIssue({ code: 'custom', message: 'Table cells must form a rectangular grid' });
    }
});
export const InsertedReportTableSchema = EditableReportTableSchema;
const ReportCustomizationReplaceOperationSchema = z.object({
    kind: z.literal('replace'),
    target_key: z.string().min(1),
    source_hash: z.string(),
    source_snapshot: z.string(),
    markdown: z.string(),
});
const ReportCustomizationHideOperationSchema = z.object({
    kind: z.literal('hide'),
    target_key: z.string().min(1),
});
const ReportCustomizationInsertOperationSchema = z.object({
    key: z.string().refine((value) => value.startsWith('custom:') && z.string().uuid().safeParse(value.slice(7)).success),
    kind: z.literal('insert'),
    parent_key: z.string().min(1),
    before_key: z.string().min(1).optional(),
    after_key: z.string().min(1).optional(),
    markdown: z.string(),
    format: z.enum(['plain', 'markdown']).optional(),
});
const ReportCustomizationInsertTableOperationSchema = z.object({
    key: z.string().refine((value) => value.startsWith('custom:') && z.string().uuid().safeParse(value.slice(7)).success),
    kind: z.literal('insert_table'),
    parent_key: z.string().min(1),
    before_key: z.string().min(1).optional(),
    after_key: z.string().min(1).optional(),
    table: InsertedReportTableSchema,
});
const ReportCustomizationReplaceTableOperationSchema = z.object({
    kind: z.literal('replace_table'),
    target_key: z.string().min(1),
    source_hash: z.string(),
    source_snapshot: z.string(),
    table: EditableReportTableSchema,
});
export const ReportCustomizationOperationSchema = z
    .discriminatedUnion('kind', [
    ReportCustomizationReplaceOperationSchema,
    ReportCustomizationReplaceTableOperationSchema,
    ReportCustomizationHideOperationSchema,
    ReportCustomizationInsertOperationSchema,
    ReportCustomizationInsertTableOperationSchema,
])
    .superRefine((operation, context) => {
    if ((operation.kind === 'insert' || operation.kind === 'insert_table') && operation.before_key === operation.after_key) {
        context.addIssue({ code: 'custom', message: 'Insert operations require one distinct anchor' });
    }
});
const ReportCustomizationBlockKindSchema = z.enum([
    'heading',
    'paragraph',
    'markdown',
    'unordered_list',
    'ordered_list',
    'table',
    'image',
    'map',
    'chart',
    'table_of_contents',
]);
export const ReportCustomizationBlockSchema = z.object({
    key: z.string().min(1),
    parent_key: z.string().min(1),
    chapter_key: z.string().min(1),
    kind: ReportCustomizationBlockKindSchema,
    markdown: z.string(),
    source_hash: z.string(),
    source_present: z.boolean(),
    state: z.enum(['generated', 'customized', 'custom', 'hidden', 'conflict']),
    software_markdown: z.string().optional(),
    custom_markdown: z.string().optional(),
    table_preview_markdown: z.string().optional(),
    table_style: z.string().optional(),
    table_autofit: z.boolean().optional(),
    table_has_header: z.boolean().optional(),
    table: EditableReportTableSchema.nullish(),
    software_table: EditableReportTableSchema.nullish(),
    custom_table: EditableReportTableSchema.nullish(),
    preview_url: z.string().optional(),
    preview_images: z
        .array(z.object({
        url: z.string(),
        label: z.string().optional(),
    }))
        .default([]),
    can_edit: z.boolean(),
    can_hide: z.boolean(),
    can_keep_mine: z.boolean(),
});
export const ReportCustomizationSchema = z.object({
    revision: z.number(),
    blocks: z.array(ReportCustomizationBlockSchema),
    operations: z.array(ReportCustomizationOperationSchema),
});
